import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import { useNavigate } from "react-router-dom";
import { takedownApi, urlApi } from "core/api";
import { ManualCheckUrl } from "core/types/takedown.types";

const fmtDate = (raw?: string | null): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("it-IT", { year: "numeric", month: "short", day: "numeric" });
};

/**
 * Coda di verifica manuale — GET /admin/takedown/manual-check (migrazione 031).
 * URL che la sonda non riesce a vedere perché una challenge anti-bot (Cloudflare) la ferma
 * sempre: il reconcile non può dire se la pagina c'è ancora. L'operatore apre il link dal proprio
 * browser e, se la pagina non c'è più, lo segna rimosso (PUT /admin/urls, fonte 'manuale').
 * Se invece è ancora online lo conferma: esce dalla coda per qualche giorno (POST
 * /admin/takedown/manual-check/online) e poi ricompare, perché la pagina può sparire quando vuole.
 * Solo console admin: il cliente continua a vedere 'online'.
 */
export const ManualCheckPanel: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [urls, setUrls] = useState<ManualCheckUrl[]>([]);
  const [disponibile, setDisponibile] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await takedownApi.getManualCheck();
        if (!active) return;
        setDisponibile(res?.disponibile !== false);
        setUrls(Array.isArray(res?.urls) ? res.urls : []);
      } catch (e: any) {
        if (active) setError(t("takedown.manual_check.error_load", { error: e?.message || e }));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Entrambi gli esiti tolgono la riga dalla coda: "rimosso" per sempre, "ancora online" per
  // qualche giorno. Si toglie solo a scrittura riuscita, altrimenti l'errore resta visibile.
  const esito = async (u: ManualCheckUrl, rimosso: boolean) => {
    setBusyId(u.url_id);
    setError(null);
    try {
      if (rimosso) await urlApi.putAdminUrls(u.url_id, "removed");
      else await takedownApi.markStillOnline(u.url_id);
      setUrls((prev) => prev.filter((x) => x.url_id !== u.url_id));
    } catch (e: any) {
      setError(t("takedown.manual_check.error_mark", { error: e?.message || e }));
    } finally {
      setBusyId(null);
    }
  };

  // Coda vuota o migrazione non applicata: niente pannello, la pagina resta quella di sempre.
  if (!loading && !error && (!disponibile || !urls.length)) return null;

  return (
    <Paper sx={{ borderRadius: "10px", p: "20px", mb: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <TravelExploreRoundedIcon color="warning" />
        <Typography variant="h6" fontWeight={700}>
          {t("takedown.manual_check.title")}
        </Typography>
        {!!urls.length && <Chip size="small" color="warning" label={urls.length} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("takedown.manual_check.description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={36} color="secondary" />
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t("takedown.manual_check.table.url")}</TableCell>
                <TableCell>{t("takedown.manual_check.table.practice")}</TableCell>
                <TableCell>{t("takedown.manual_check.table.since")}</TableCell>
                <TableCell align="right">{t("takedown.manual_check.table.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {urls.map((u) => (
                <TableRow key={u.url_id} hover>
                  <TableCell sx={{ maxWidth: 480 }}>
                    <Tooltip title={u.title || u.url}>
                      <Link
                        href={u.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        noWrap
                        // index.css mette `a:link/:visited/:hover { color: white }` su tutta l'app
                        // e batte la classe MUI del Link: senza questi selettori l'URL era bianco
                        // su bianco.
                        sx={{
                          display: "block",
                          "&:link, &:visited, &:hover": { color: "text.primary" },
                        }}
                      >
                        {u.url}
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {u.practice_ids.map((pid) => (
                        <Chip
                          key={pid}
                          size="small"
                          variant="outlined"
                          label={`#${pid}`}
                          onClick={() => navigate(`/web-rep/takedown/${pid}`)}
                        />
                      ))}
                      {!u.practice_ids.length && "—"}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={t("takedown.manual_check.table.since_hint", {
                        status: u.last_http_status ?? "—",
                        checked: fmtDate(u.last_reconcile_at),
                      })}
                    >
                      <span>{fmtDate(u.antibot_since)}</span>
                    </Tooltip>
                    {u.checked_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {t("takedown.manual_check.table.last_seen_online", {
                          date: fmtDate(u.checked_at),
                          who: u.checked_by || "—",
                        })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {busyId === u.url_id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={busyId !== null}
                          onClick={() => esito(u, true)}
                        >
                          {t("takedown.manual_check.mark_removed")}
                        </Button>
                        <Tooltip title={t("takedown.manual_check.still_online_hint")}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              disabled={busyId !== null}
                              onClick={() => esito(u, false)}
                            >
                              {t("takedown.manual_check.still_online")}
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ManualCheckPanel;
